/**
 * PURPOSE: Revisión final + confirmación explícita antes de publicar un Comunicado.
 * RESPONSIBILITIES: Mostrar título/mensaje/adjunto/audiencia tal como se enviarán, advertir que la
 *   publicación es un envío masivo irreversible, y proteger contra doble clic mientras la solicitud
 *   está en curso (FTR-0005 §17/§26). Un 409 (ya publicado/oculto por otra pestaña) refresca el
 *   estado real en vez de sugerir reintentar.
 * COLLABORATORS: HayllisCommunicationsPanel.jsx, hayllisNotificationsService.js.
 * ROUTE: winscore/src/admin/HayllisCommunications/HayllisPublishConfirmModal.jsx
 */
import React, { useState } from "react";
import toast from "react-hot-toast";

import {
  getApiErrorMessage,
  getHayllisCommunicationDetail,
  publishHayllisCommunication,
} from "../../services/hayllisNotificationsService.js";
import { buildPushSummary, formatBytes } from "./hayllisCommunicationsDisplay.utils.js";

const HayllisPublishConfirmModal = ({ notification, onCancel, onPublished }) => {
  const [publishing, setPublishing] = useState(false);

  if (!notification) return null;

  const handleClose = () => {
    if (publishing) return;
    onCancel();
  };

  const handleConfirm = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const published = await publishHayllisCommunication(notification.id);
      toast.success(`Publicado correctamente. ${buildPushSummary(published)}`);
      onPublished(published);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo publicar el comunicado."));
      if (error?.response?.status === 409) {
        // Someone else already published/hid it — never offer "Publicar" again on stale state,
        // refresh and show what's really there (FTR-0005 §20).
        try {
          const fresh = await getHayllisCommunicationDetail(notification.id);
          onPublished(fresh);
        } catch (refreshError) {
          console.error("No se pudo refrescar el comunicado tras el 409", refreshError);
        }
      }
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/50 p-4"
      onMouseDown={handleClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-confirm-title"
      >
        <h3 id="publish-confirm-title" className="text-xl font-bold text-slate-800 dark:text-white">
          Publicar comunicado
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Revisa el contenido antes de enviarlo. Esta acción no se puede deshacer.
        </p>

        <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-gray-800">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Título</p>
            <p className="font-semibold text-slate-800 dark:text-white">{notification.title}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Mensaje</p>
            <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">{notification.body}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Adjunto</p>
            <p className="text-sm text-slate-700 dark:text-slate-200">
              {notification.attachment
                ? `${notification.attachment.name}${formatBytes(notification.attachment.size) ? ` (${formatBytes(notification.attachment.size)})` : ""}`
                : "Sin adjunto"}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Audiencia</p>
            <p className="text-sm text-slate-700 dark:text-slate-200">Todos</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
          Este comunicado se publicará y se enviará como notificación push a todos los dispositivos que
          actualmente tengan las notificaciones de Hayllis habilitadas.
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={publishing}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:text-slate-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={publishing}
            className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {publishing ? "Publicando..." : "Publicar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HayllisPublishConfirmModal;
